import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { logEmployeeActivity } from "../utils/activityLogger";
import "./Packaging.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const PACKAGING_API = `${API_BASE}/packaging`;
const SUPPLIER_API = `${API_BASE}/suppliers`;
const PACKAGING_NET_WORTH_EVENT = "packaging-networth-updated";
const PACKAGING_NET_WORTH_STORAGE_KEY = "packaging_networth_updated_at";

const notifyPackagingNetWorth = () => {
  window.dispatchEvent(new Event(PACKAGING_NET_WORTH_EVENT));
  localStorage.setItem(PACKAGING_NET_WORTH_STORAGE_KEY, String(Date.now()));
};

const defaultForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  material: "",
  size_variant: "",
  quantity: "",
  cost_per_unit: "",
  supplier_name: "",
  account_name: "",
});

const formatPackagingDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("en-IN");
};

export default function Packaging() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [editId, setEditId] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(defaultForm());

  const fetchEntries = async () => {
    try {
      const res = await axios.get(PACKAGING_API);
      setEntries(res.data || []);
    } catch (error) {
      console.error("Error fetching packaging entries:", error);
      setEntries([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/accounts`);
      setAccounts(res.data || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(SUPPLIER_API);
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
    }
  };

  const fetchMaterials = async () => {
    setMaterials([
      "Bubble Wrap",
      "Carton Box",
      "Adhesive Tape",
      "Packing Peanuts",
      "Kraft Paper",
      "Styrofoam",
      "Tissue Paper",
      "Corrugated Box",
      "Plastic Wrap",
      "Other",
    ]);
  };

  useEffect(() => {
    fetchEntries();
    fetchAccounts();
    fetchSuppliers();
    fetchMaterials();
  }, []);

  const totalCost = (Number(form.quantity || 0) * Number(form.cost_per_unit || 0)).toFixed(2);

  const resetForm = () => {
    setForm(defaultForm());
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.date) return alert("Date required");
    if (!form.material) return alert("Material required");
    if (!form.size_variant) return alert("Size/Variant required");
    if (!form.quantity) return alert("Quantity required");
    if (!form.cost_per_unit) return alert("Cost per unit required");
    if (!form.supplier_name) return alert("Supplier name required");

    try {
      const payload = {
        entry_date: form.date,
        material: form.material,
        size_variant: form.size_variant,
        quantity: Number(form.quantity),
        cost_per_unit: Number(form.cost_per_unit),
        total_cost: Number(totalCost),
        supplier_name: form.supplier_name,
        account_name: form.account_name,
      };

      if (editId) {
        await axios.put(`${PACKAGING_API}/${editId}`, payload);
        void logEmployeeActivity({
          action: "Update Packaging Entry",
          module: "Packaging",
          description: `Updated packaging entry for ${form.material} (${form.size_variant}) with quantity ${form.quantity}.`,
        });
      } else {
        await axios.post(PACKAGING_API, payload);
        void logEmployeeActivity({
          action: "Add Packaging Entry",
          module: "Packaging",
          description: `Added packaging entry for ${form.material} (${form.size_variant}) with quantity ${form.quantity}.`,
        });
      }

      resetForm();
      fetchEntries();
      notifyPackagingNetWorth();
    } catch (error) {
      console.error("Error saving packaging entry:", error);
      alert("Failed to save entry");
    }
  };

  const handleEdit = (entry) => {
    setForm({
      date: entry.date || "",
      material: entry.material || "",
      size_variant: entry.size_variant || "",
      quantity: String(entry.quantity || ""),
      cost_per_unit: String(entry.cost_per_unit || ""),
      supplier_name: entry.supplier_name || "",
      account_name: entry.account_name || "",
    });
    setEditId(entry.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      await axios.delete(`${PACKAGING_API}/${id}`);
      const deletedEntry = entries.find((entry) => entry.id === id);
      void logEmployeeActivity({
        action: "Delete Packaging Entry",
        module: "Packaging",
        description: `Deleted packaging entry for ${deletedEntry?.material || "Unknown Material"} (${deletedEntry?.size_variant || "-"}) with quantity ${deletedEntry?.quantity || 0}.`,
      });
      fetchEntries();
      notifyPackagingNetWorth();
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("Failed to delete entry");
    }
  };

  const handleExportCSV = () => {
    if (entries.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Date",
      "Material",
      "Size/Variant",
      "Quantity",
      "Cost/Unit",
      "Total Cost",
      "Supplier",
      "Status",
    ];

    const rows = entries.map((entry) => [
      entry.date || "",
      entry.material || "",
      entry.size_variant || "",
      entry.quantity || "",
      `Rs.${Number(entry.cost_per_unit || 0).toFixed(2)}`,
      `Rs.${Number(entry.total_cost || 0).toFixed(2)}`,
      entry.supplier_name || "",
      entry.status || "Active",
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "packaging_entries.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="packaging-page">
      <div className="packaging-page__header">
        <div>
          <h2>Packaging Management</h2>
          <p>Manage packaging purchases in the same clean structure used on Sales.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="packaging-card packaging-form">
        <div className="packaging-section-title">
          <div>
            <h3>Add Packaging Entry</h3>
            <p>Record new material purchases in a compact Sales-style form.</p>
          </div>
          <div className="packaging-title-actions">
            <button
              type="button"
              className="packaging-history-btn"
              onClick={() => navigate("/packaging/history")}
            >
              Packaging History
            </button>
          </div>
        </div>

        <div className="packaging-form-shell">
          <div className="packaging-form-grid">
            <div className="packaging-field">
            <label htmlFor="packaging-entry-date">Date</label>
            <input
              id="packaging-entry-date"
              className="packaging-form-control"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-material">Material</label>
            <select
              id="packaging-entry-material"
              className="packaging-form-control"
              value={form.material}
              onChange={(e) => setForm({ ...form, material: e.target.value })}
            >
              <option value="">Select Material</option>
              {materials.map((mat) => (
                <option key={mat} value={mat}>
                  {mat}
                </option>
              ))}
            </select>
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-size-variant">Size/Variant</label>
            <input
              id="packaging-entry-size-variant"
              className="packaging-form-control"
              type="text"
              placeholder="e.g. 10x10, 2-Ply"
              value={form.size_variant}
              onChange={(e) => setForm({ ...form, size_variant: e.target.value })}
            />
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-quantity">Quantity</label>
            <input
              id="packaging-entry-quantity"
              className="packaging-form-control"
              type="number"
              placeholder="0"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-cost-per-unit">Cost Per Unit (Rs)</label>
            <input
              id="packaging-entry-cost-per-unit"
              className="packaging-form-control"
              type="number"
              placeholder="0.00"
              value={form.cost_per_unit}
              onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })}
            />
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-total-cost">Total Cost</label>
            <input
              id="packaging-entry-total-cost"
              type="text"
              value={`Rs.${totalCost}`}
              disabled
              className="packaging-form-control packaging-auto-calc"
            />
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-supplier-name">Supplier Name</label>
            <select
              id="packaging-entry-supplier-name"
              className="packaging-form-control"
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
            >
              <option value="">Select Supplier</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.supplier_name}>
                  {supplier.supplier_name}
                </option>
              ))}
            </select>
            </div>

            <div className="packaging-field">
            <label htmlFor="packaging-entry-account">Account</label>
              <select
                id="packaging-entry-account"
                className="packaging-form-control"
                value={form.account_name}
                onChange={(e) => setForm({ ...form, account_name: e.target.value })}
              >
                <option value="">Select Account</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.account_name}>
                    {acc.account_name}
                  </option>
                ))}
              </select>
          </div>
          </div>
        </div>

        <div className="packaging-form-actions">
          <button type="submit" className="packaging-save-btn">
            {editId ? "Update Entry" : "Save Entry"}
          </button>
          {editId && (
            <button type="button" onClick={resetForm} className="packaging-cancel-btn">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="packaging-card packaging-table-card">
        <div className="packaging-table-head">
          <div>
            <h3>Packaging Expense Table</h3>
            <p>Browse all packaging purchases in the same lower-table structure as Sales.</p>
          </div>
          <button type="button" onClick={handleExportCSV} className="packaging-export-btn">
            Export CSV
          </button>
        </div>

        <div className="packaging-table-scroll">
          <table className="packaging-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Material</th>
                <th>Size</th>
                <th>Quantity</th>
                <th>Cost/Unit</th>
                <th>Total Cost</th>
                <th>Supplier</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan="9" className="packaging-empty-state">
                    No packaging entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatPackagingDateTime(entry.created_at || entry.entry_date || entry.date)}</td>
                    <td className="packaging-table-strong">{entry.material || "-"}</td>
                    <td>{entry.size_variant || "-"}</td>
                    <td>{entry.quantity || 0}</td>
                    <td className="packaging-table-price">
                      Rs.{Number(entry.cost_per_unit || 0).toFixed(2)}
                    </td>
                    <td className="packaging-table-strong">
                      Rs.{Number(entry.total_cost || 0).toFixed(2)}
                    </td>
                    <td>{entry.supplier_name || "-"}</td>
                    <td>
                      <span className={`packaging-status-badge ${entry.status || "active"}`}>
                        {entry.status || "Added"}
                      </span>
                    </td>
                    <td>
                      <div className="packaging-actions-cell">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="packaging-edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry.id)}
                          className="packaging-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
